import { useState, type ChangeEvent } from 'react';
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { formatGrosze } from '@/lib/format';
import { useAuth } from '@/lib/auth/AuthContext';
import { useDaniaRestauracji, useZdolnosci } from '@/lib/useAnalytics';
import type { DanieRestauracji } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * DANIA RESTAURACJI — lista i edycja karty dań.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ TRZY STANY, NIE DWA ───────────────────────────────────────────────────────────────────────┐
 * │ Danie ma stan „oczekuje", „zatwierdzone" albo „odrzucone" — w źródle `IsConfirmed` jest      │
 * │ typu `bool?`. Restauracja MUSI widzieć różnicę: „czeka na decyzję" i „odrzucone" to dwie     │
 * │ różne sytuacje, a przy sklejeniu ich w jedno restauracja czekałaby na decyzję, która już     │
 * │ zapadła.                                                                                     │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * ZMIANA WRACA DO KOLEJKI. Każda edycja dania przechodzi przez moderację — restauracja nie
 * publikuje zmian sama. Formularz mówi to wprost, żeby nikt nie liczył na natychmiastowy skutek.
 *
 * PRZYCISKI ZALEŻĄ OD ZDOLNOŚCI ŚRODOWISKA, nie od nadziei. Gdy zapisu nie da się wykonać,
 * przycisk jest wyłączony i podaje powód — próba „na sucho" kończyłaby się błędem, którego
 * operator nie umie zinterpretować.
 */

const OPIS_STANU: Record<string, { etykieta: string; kolor: 'warning' | 'success' | 'default' }> = {
  OCZEKUJE: { etykieta: 'oczekuje na decyzję', kolor: 'warning' },
  ZATWIERDZONE: { etykieta: 'zatwierdzone', kolor: 'success' },
  ODRZUCONE: { etykieta: 'odrzucone', kolor: 'default' },
};

/** Walidacja lustrzana do serwerowej. Front łapie wcześniej, serwer rozstrzyga. */
function sprawdz(f: { nazwa: string; cena: string; ilosc: string }) {
  const usterki: Record<string, string> = {};
  if (f.nazwa.trim() === '') usterki.nazwa = 'Nazwa jest wymagana.';
  if (f.nazwa.length > 255) usterki.nazwa = 'Nazwa dłuższa niż 255 znaków.';

  const cena = Number(f.cena.replace(',', '.'));
  if (f.cena.trim() === '' || !Number.isFinite(cena) || cena < 0) {
    usterki.cena = 'Podaj cenę w złotych, nieujemną.';
  }
  if (f.ilosc.trim() !== '') {
    const n = Number(f.ilosc);
    if (!Number.isInteger(n) || n < 0) usterki.ilosc = 'Ilość musi być liczbą nieujemną.';
  }
  return usterki;
}

function FormularzDania({
  danie, otwarte, onZamknij, zapisMozliwy, powodBraku,
}: {
  danie: DanieRestauracji | null;
  otwarte: boolean;
  onZamknij: () => void;
  zapisMozliwy: boolean;
  powodBraku: string;
}) {
  const [pola, setPola] = useState({
    nazwa: danie?.nazwa ?? '',
    opis: danie?.opis ?? '',
    skladniki: danie?.skladniki ?? '',
    cena: danie?.cena_grosze !== null && danie?.cena_grosze !== undefined
      ? (danie.cena_grosze / 100).toFixed(2) : '',
    ilosc: danie?.ilosc !== null && danie?.ilosc !== undefined ? String(danie.ilosc) : '',
  });
  const [dotkniete, setDotkniete] = useState(false);

  const usterki = sprawdz(pola);
  const poprawne = Object.keys(usterki).length === 0;
  const zmien = (k: keyof typeof pola) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDotkniete(true);
      setPola((p) => ({ ...p, [k]: e.target.value }));
    };

  return (
    <Dialog open={otwarte} onClose={onZamknij} fullWidth maxWidth="sm">
      <DialogTitle>{danie ? 'Edycja dania' : 'Nowe danie'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/*
            Powiedziane PRZED wypełnieniem formularza, nie po kliknięciu „Zapisz".
            Restauracja ma wiedzieć, czego się spodziewać, zanim włoży w to pracę.
          */}
          <Alert severity="info">
            Zmiana trafia do <strong>moderacji</strong> i pojawi się w karcie dopiero po
            zatwierdzeniu. Danie wraca wtedy do stanu „oczekuje".
          </Alert>

          <TextField
            label="Nazwa" size="small" value={pola.nazwa} onChange={zmien('nazwa')}
            error={dotkniete && Boolean(usterki.nazwa)}
            helperText={dotkniete ? usterki.nazwa : undefined}
            required
          />
          <TextField label="Opis" size="small" multiline minRows={2}
            value={pola.opis} onChange={zmien('opis')} />
          <TextField label="Składniki" size="small" multiline minRows={2}
            value={pola.skladniki} onChange={zmien('skladniki')} />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Cena (zł)" size="small" value={pola.cena} onChange={zmien('cena')}
              error={dotkniete && Boolean(usterki.cena)}
              helperText={dotkniete ? usterki.cena : undefined}
              required
            />
            <TextField
              label="Ilość" size="small" value={pola.ilosc} onChange={zmien('ilosc')}
              error={dotkniete && Boolean(usterki.ilosc)}
              helperText={dotkniete ? usterki.ilosc : undefined}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onZamknij}>Anuluj</Button>
        {/*
          POWÓD BLOKADY STOI NA WIDOKU, NIE POD NAJAZDEM MYSZY.
          Ten panel bywa używany na tablecie w kuchni, gdzie najazdu po prostu nie ma —
          a wyszarzony przycisk bez wyjaśnienia jest zagadką, nie informacją.
        */}
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {!zapisMozliwy && (
            <Typography variant="caption" sx={{ color: 'var(--uwaga)', maxWidth: 360 }}>
              {powodBraku}
            </Typography>
          )}
          <Button variant="contained" disabled={!zapisMozliwy || !poprawne}>
            Zapisz i wyślij do moderacji
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export function DaniaPage() {
  const auth = useAuth();
  const czyWlasciciel = auth.has('admin.users.read');
  const [restauracja, setRestauracja] = useState('');
  const [edytowane, setEdytowane] = useState<DanieRestauracji | null>(null);
  const [formularz, setFormularz] = useState(false);

  const wskazana = restauracja.trim() === '' ? undefined : Number(restauracja);
  const { data, isLoading, error } = useDaniaRestauracji(wskazana);
  const zdolnosci = useZdolnosci();

  const zapisMozliwy = zdolnosci.data?.zdolnosci.dania_zapis ?? false;
  const brakujaceOperacje = zdolnosci.data?.brakujace.operacje_api ?? [];
  const powodBraku = brakujaceOperacje.find((o) => /dania/i.test(o.nazwa))
    ? 'System główny nie ma administracyjnej operacji zapisu dań — zgłoszone do strumienia R5-03.'
    : 'Zapis nie jest w tym środowisku podłączony.';

  const brakWskazania = czyWlasciciel && wskazana === undefined;
  const dania = data?.dania ?? [];

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Karta dań"
        opis="Karta dań restauracji. Każda zmiana przechodzi przez moderację."
      />

      {data?.zaleznosc && (
        <Alert severity="warning">
          <strong>Karta dań nie ma jeszcze źródła.</strong> {data.zaleznosc.skutek}{' '}
          Brakuje: <code>{data.zaleznosc.czego_brak}</code> (strumień {data.zaleznosc.strumien}).
        </Alert>
      )}

      {!zapisMozliwy && (
        <Alert severity="info">
          <strong>Edycja dań jest chwilowo niedostępna.</strong> {powodBraku} Podgląd karty
          działa normalnie.
        </Alert>
      )}

      {czyWlasciciel && (
        <Karta>
          <Stack direction="row" spacing={2} sx={{ p: 2, alignItems: 'center' }}>
            <TextField label="Restauracja (nr)" size="small" sx={{ width: 180 }} value={restauracja}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRestauracja(e.target.value)}
              helperText="Konto właściciela nie ma własnej restauracji" />
            <Tooltip title={zapisMozliwy ? '' : powodBraku}>
              <span>
                <Button variant="contained" disabled={!zapisMozliwy}
                  onClick={() => { setEdytowane(null); setFormularz(true); }}>
                  Nowe danie
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Karta>
      )}

      {brakWskazania && (
        <EmptyState
          title="Wskaż restaurację"
          description="Konto właściciela nie jest przypisane do restauracji, więc trzeba podać, czyją kartę oglądasz."
        />
      )}

      {!brakWskazania && isLoading && <LoadingState />}
      {!brakWskazania && error ? <ErrorState error={error} context="Panel restauracji — dania." /> : null}

      {!brakWskazania && !isLoading && !error && dania.length === 0 && (
        <EmptyState
          title={data?.zrodlo_podlaczone ? 'Karta dań jest pusta' : 'Źródło nie jest podłączone'}
          description={data?.zrodlo_podlaczone
            ? 'Ta restauracja nie ma jeszcze żadnego dania w karcie.'
            : 'Pusta lista NIE znaczy, że restauracja nie ma dań — znaczy, że Control Center jeszcze ich nie widzi.'}
        />
      )}

      {dania.length > 0 && (
        <Karta>
          <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Karta dań restauracji">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Nazwa</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Składniki</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cena</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Ilość</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Stan</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dania.map((d) => {
                  const stan = OPIS_STANU[d.stan] ?? { etykieta: d.stan, kolor: 'default' as const };
                  return (
                    <TableRow key={d.danie_id} hover>
                      <TableCell>{d.nazwa ?? '(bez nazwy)'}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {d.skladniki ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {/* Brak ceny to brak ceny — nie „0 zł". Danie bez ceny jest podejrzane. */}
                        {d.cena_grosze === null
                          ? <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>nieznana</Typography>
                          : formatGrosze(d.cena_grosze)}
                      </TableCell>
                      <TableCell align="right">{d.ilosc ?? '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={stan.etykieta} color={stan.kolor}
                          variant={d.stan === 'OCZEKUJE' ? 'filled' : 'outlined'} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={zapisMozliwy ? '' : powodBraku}>
                          <span>
                            <Button size="small" disabled={!zapisMozliwy}
                              onClick={() => { setEdytowane(d); setFormularz(true); }}>
                              Edytuj
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Karta>
      )}

      {formularz && (
        <FormularzDania
          // `key` wymusza świeży stan formularza przy zmianie dania — bez tego pola
          // zostałyby wypełnione wartościami poprzedniego.
          key={edytowane?.danie_id ?? 'nowe'}
          danie={edytowane}
          otwarte={formularz}
          onZamknij={() => setFormularz(false)}
          zapisMozliwy={zapisMozliwy}
          powodBraku={powodBraku}
        />
      )}
    </Stack>
  );
}

export default DaniaPage;
export { sprawdz as sprawdzDanieFront, OPIS_STANU };
